import type { AdminAppealListItemDto } from '@carcheck/shared';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { initials, shortId } from './displayName';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { formatDate } from '../../../utils/format';
import { AppealPriorityBadge } from '../_components/AppealPriorityBadge';
import { AppealStatusBadge } from '../_components/AppealStatusBadge';

/**
 * Column definitions for the appeals moderation DataGrid (AUT-130),
 * extracted from the page so the page component stays under the line
 * cap. Sorting is disabled per column — the backend list is fixed
 * `createdAt DESC`, so the grid never sends a sort the server can't honour.
 *
 * Columns mirror the mockup `appeals-queue.html`: Апелляция / Заказ /
 * Заказчик / Причина / Приоритет / Статус / Создана (7 columns).
 *
 * The customer cell (the filing party — the mockup's «Заказчик» column)
 * renders through `<RedactedField perm="view_pii">`, which agrees with
 * `row.piiRevealed` (both derive from the same backend permission). A
 * revealed value is never re-redacted; only revealed values get a copy
 * button. The accused inspector's PII lands on the detail drawer (dual
 * identity), not the list (mockup-aligned — keeps the queue scannable).
 */

/** Customer cell: avatar (2-letter initials) + name (PII-gated). */
const CustomerCell = ({ row, t }: { row: AdminAppealListItemDto; t: TFunction }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
    <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700 }}>
      {initials(row.customerName)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      {row.customerName ? (
        <Typography variant="body2" fontWeight={600} noWrap component="span">
          <RedactedField
            perm="view_pii"
            value={row.customerName}
            redacted={row.customerName}
            {...(row.piiRevealed ? { copyable: true } : {})}
          />
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ color: tokens.text.tertiary }} noWrap>
          {t('appeals.detail.noValue')}
        </Typography>
      )}
    </Box>
  </Box>
);

/** Appeal-number + short id cell (mono font per mockup `.u-id` style). */
const AppealCell = ({ row }: { row: AdminAppealListItemDto }): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
    <Typography variant="body2" fontWeight={600} fontFamily="monospace">
      {`№ ${row.appealNumber}`}
    </Typography>
    <Typography variant="caption" sx={{ color: tokens.text.tertiary }} fontFamily="monospace">
      {shortId(row.id)}
    </Typography>
  </Box>
);

export const buildAppealColumns = (t: TFunction): GridColDef<AdminAppealListItemDto>[] => [
  {
    field: 'appeal',
    headerName: t('appeals.columns.appeal'),
    flex: 1,
    minWidth: 170,
    sortable: false,
    renderCell: ({ row }) => <AppealCell row={row} />,
  },
  {
    field: 'order',
    headerName: t('appeals.columns.order'),
    flex: 0.6,
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" fontFamily="monospace">
        {`№ ${row.orderId.slice(0, 8)}`}
      </Typography>
    ),
  },
  {
    field: 'customer',
    headerName: t('appeals.columns.customer'),
    flex: 1.1,
    minWidth: 190,
    sortable: false,
    renderCell: ({ row }) => <CustomerCell row={row} t={t} />,
  },
  {
    field: 'reason',
    headerName: t('appeals.columns.reason'),
    flex: 1,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" sx={{ color: tokens.text.secondary }} noWrap>
        {t(`appeals.reason.${row.reason}`)}
      </Typography>
    ),
  },
  {
    field: 'priority',
    headerName: t('appeals.columns.priority'),
    flex: 0.6,
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }) => <AppealPriorityBadge priority={row.priority} />,
  },
  {
    field: 'status',
    headerName: t('appeals.columns.status'),
    flex: 0.7,
    minWidth: 140,
    sortable: false,
    renderCell: ({ row }) => <AppealStatusBadge status={row.status} />,
  },
  {
    field: 'createdAt',
    headerName: t('appeals.columns.createdAt'),
    flex: 0.6,
    minWidth: 120,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
        {formatDate(row.createdAt)}
      </Typography>
    ),
  },
];
